import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Clock, DollarSign, MapPin, Users, Trophy, FileText } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, tournamentsAPI, tournamentSeriesAPI, venuesAPI, handleApiError } from '../utils/api';

// Valeur spéciale du select "série" pour déclencher l'affichage du champ de
// création d'une nouvelle série, plutôt que de rattacher à une existante.
const NEW_SERIES_VALUE = '__new__';

const EMPTY_DETAILED_INFO = {
  venueId: '',
  seriesId: '',
  location: '',
  duration: '',
  organizer: '',
  buyIn: '',
  buyInAmount: '',
  prizePool: '',
  startingStack: '',
  blindStructure: '',
  agenda: [''],
  notes: '',
  // Par défaut, un day a son propre buy-in/inscription (comportement
  // historique). À décocher pour un day comme un Final Day atteint sans
  // nouvelle inscription (voir V19) : les champs "Mes entrées"/"Buy-in
  // payé" et le bouton "Enregistrer" sont alors masqués pour ce day dans
  // "Mes participations".
  hasOwnBuyIn: true
};

const CreateEditTournamentPage = () => {
  const navigate = useNavigate();
  const { festivalId, tournamentId } = useParams();
  const { userRole } = useAuth();
  const isEditMode = !!tournamentId;

  const [festival, setFestival] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    detailedInfo: { ...EMPTY_DETAILED_INFO }
  });

  const [errors, setErrors] = useState({});
  const [venues, setVenues] = useState([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [venuesError, setVenuesError] = useState(null);
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState(null);
  const [newSeriesName, setNewSeriesName] = useState('');
  // Nombre total d'entrées de la série entière (ex: "450 entrées au Main
  // Event"), distinct de entriesCount qui est propre à chaque participant.
  // Synchronisé automatiquement avec la série sélectionnée (voir l'effet
  // ci-dessous), modifiable à tout moment par l'admin.
  const [seriesTotalEntries, setSeriesTotalEntries] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadVenues = useCallback(async () => {
    setVenuesLoading(true);
    setVenuesError(null);
    try {
      const response = await venuesAPI.getAll();
      setVenues(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setVenuesError(`${errTitle} : ${details}`);
    } finally {
      setVenuesLoading(false);
    }
  }, []);

  const loadSeries = useCallback(async () => {
    setSeriesLoading(true);
    setSeriesError(null);
    try {
      const response = await tournamentSeriesAPI.getAll(festivalId);
      setSeries(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setSeriesError(`${errTitle} : ${details}`);
    } finally {
      setSeriesLoading(false);
    }
  }, [festivalId]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const festivalResponse = await festivalsAPI.getById(festivalId);
      setFestival(festivalResponse.data);

      if (isEditMode) {
        const tournamentResponse = await tournamentsAPI.getById(festivalId, tournamentId);
        const tournament = tournamentResponse.data;
        setFormData({
          title: tournament.title || '',
          description: tournament.description || '',
          date: tournament.date || '',
          time: tournament.time || '',
          detailedInfo: {
            ...EMPTY_DETAILED_INFO,
            ...tournament.detailedInfo,
            venueId: tournament.detailedInfo?.venueId || '',
            seriesId: tournament.detailedInfo?.seriesId || '',
            agenda: tournament.detailedInfo?.agenda?.length ? tournament.detailedInfo.agenda : [''],
            hasOwnBuyIn: tournament.detailedInfo?.hasOwnBuyIn !== false,
            // Montant numérique ; le texte libre d'origine n'est conservé que
            // pour un tournoi dont le buy-in n'a pas pu être converti (V22).
            buyInAmount: tournament.detailedInfo?.buyInAmount ?? '',
            buyIn: tournament.detailedInfo?.buyInAmount != null ? '' : (tournament.detailedInfo?.buyIn || '')
          }
        });
      } else {
        setFormData(prev => ({
          ...prev,
          date: festivalResponse.data.startDate
        }));
      }
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setLoadError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [festivalId, tournamentId, isEditMode]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Synchronise le champ "nombre total d'entrées" avec la série
  // sélectionnée : à chaque changement de sélection (y compris au premier
  // chargement en mode édition), on reprend la valeur déjà connue pour
  // cette série plutôt que de laisser un chiffre d'une autre série affiché
  // par erreur. Laissé tel quel pour "+ Créer une nouvelle série..." : la
  // valeur saisie est alors celle de la future série.
  useEffect(() => {
    const selectedSeriesId = formData.detailedInfo.seriesId;
    if (selectedSeriesId === NEW_SERIES_VALUE) {
      return;
    }
    if (!selectedSeriesId) {
      setSeriesTotalEntries('');
      return;
    }
    const matchingSeries = series.find((s) => String(s.id) === String(selectedSeriesId));
    setSeriesTotalEntries(
      matchingSeries && matchingSeries.totalEntries != null ? String(matchingSeries.totalEntries) : ''
    );
  }, [formData.detailedInfo.seriesId, series]);

  // Vérification admin - doit être après les hooks
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement...
        </div>
      </div>
    );
  }

  if (loadError || !festival) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              {loadError ? 'Erreur' : 'Festival non trouvé'}
            </h2>
            {loadError && <p className="text-gray-600 mb-6">{loadError}</p>}
            <button onClick={() => navigate('/manage-festivals')} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    if (name.startsWith('detailedInfo.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        detailedInfo: {
          ...prev.detailedInfo,
          [field]: fieldValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleArrayChange = (arrayName, index, value) => {
    setFormData(prev => ({
      ...prev,
      detailedInfo: {
        ...prev.detailedInfo,
        [arrayName]: prev.detailedInfo[arrayName].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addArrayItem = (arrayName) => {
    setFormData(prev => ({
      ...prev,
      detailedInfo: {
        ...prev.detailedInfo,
        [arrayName]: [...prev.detailedInfo[arrayName], '']
      }
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      detailedInfo: {
        ...prev.detailedInfo,
        [arrayName]: prev.detailedInfo[arrayName].filter((_, i) => i !== index)
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Le titre est requis';
    if (!formData.description.trim()) newErrors.description = 'La description est requise';
    if (!formData.date) newErrors.date = 'La date est requise';
    if (!formData.time) newErrors.time = 'L\'heure est requise';

    // Vérifier que la date est dans la période du festival
    if (formData.date && (formData.date < festival.startDate || formData.date > festival.endDate)) {
      newErrors.date = `La date doit être entre le ${new Date(festival.startDate).toLocaleDateString('fr-FR')} et le ${new Date(festival.endDate).toLocaleDateString('fr-FR')}`;
    }

    if (formData.detailedInfo.seriesId === NEW_SERIES_VALUE && !newSeriesName.trim()) {
      newErrors.seriesId = 'Indique un nom pour la nouvelle série, ou choisis "Tournoi indépendant"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Si l'admin a choisi de créer une nouvelle série, on la crée d'abord
      // pour récupérer son ID, avant de l'utiliser sur le tournoi lui-même.
      // S'il a choisi une série existante, on met à jour son nombre total
      // d'entrées au passage (saisi/corrigé ici, voir seriesTotalEntries).
      let seriesId = formData.detailedInfo.seriesId;
      const parsedTotalEntries = seriesTotalEntries === '' ? null : parseInt(seriesTotalEntries, 10);
      if (seriesId === NEW_SERIES_VALUE) {
        const seriesResponse = await tournamentSeriesAPI.create(festivalId, newSeriesName.trim(), parsedTotalEntries);
        seriesId = seriesResponse.data.id;
      } else if (seriesId) {
        const existingSeries = series.find((s) => String(s.id) === String(seriesId));
        if (existingSeries) {
          await tournamentSeriesAPI.update(festivalId, seriesId, {
            name: existingSeries.name,
            totalEntries: parsedTotalEntries,
          });
        }
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        time: formData.time,
        detailedInfo: {
          ...formData.detailedInfo,
          venueId: formData.detailedInfo.venueId ? parseInt(formData.detailedInfo.venueId) : null,
          seriesId: seriesId ? parseInt(seriesId) : null,
          buyInAmount: formData.detailedInfo.buyInAmount === '' ? null : parseFloat(formData.detailedInfo.buyInAmount),
          agenda: formData.detailedInfo.agenda.filter(item => item.trim() !== '')
        }
      };

      if (isEditMode) {
        await tournamentsAPI.update(festivalId, tournamentId, payload);
      } else {
        await tournamentsAPI.create(festivalId, payload);
      }

      navigate(`/manage-festivals/${festivalId}/tournaments`);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setErrors(prev => ({ ...prev, submit: `${errTitle} : ${details}` }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments`)}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour aux tournois
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className={`${festival.circuit?.color || 'bg-blue-600'} bg-opacity-20 border-l-8 ${(festival.circuit?.color || 'bg-blue-600').replace('bg-', 'border-')} p-6`}>
              <h1 className="text-3xl font-bold flex items-center text-gray-800 mb-2">
                <Trophy className="mr-3" size={32} />
                {isEditMode ? 'Modifier le Tournoi' : 'Créer un Nouveau Tournoi'}
              </h1>
              <p className="text-gray-600">Festival : {festival.title}</p>
            </div>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {errors.submit}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-6">
              {/* Informations de base */}
              <div className="border-b pb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Informations de base</h2>

                <div className="space-y-4">
                  {/* Titre */}
                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <Trophy className="mr-2 text-blue-600" size={18} />
                      Titre du Tournoi *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Ex: Main Event Day 1A - 500€"
                      className={`w-full px-4 py-3 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <FileText className="mr-2 text-blue-600" size={18} />
                      Description *
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Ex: NLH Deepstack Championship - Structure Premium"
                      className={`w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                  </div>

                  {/* Date et heure */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                        <Calendar className="mr-2 text-blue-600" size={18} />
                        Date *
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        min={festival.startDate}
                        max={festival.endDate}
                        className={`w-full px-4 py-3 border ${errors.date ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                    </div>

                    <div>
                      <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                        <Clock className="mr-2 text-blue-600" size={18} />
                        Heure *
                      </label>
                      <input
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.time ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Détails du tournoi */}
              <div className="border-b pb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Détails du tournoi</h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="mr-2 text-blue-600" size={18} />
                      Lieu (casino)
                    </label>
                    {venuesError && (
                      <p className="text-red-500 text-sm mb-2">{venuesError}</p>
                    )}
                    <select
                      name="detailedInfo.venueId"
                      value={formData.detailedInfo.venueId}
                      onChange={handleChange}
                      disabled={venuesLoading}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">
                        {venuesLoading ? 'Chargement des lieux...' : "Aucun lieu de la liste (utiliser le champ texte ci-dessous)"}
                      </option>
                      {venues.map((venue) => (
                        <option key={venue.id} value={venue.id}>
                          {venue.name}{venue.city ? ` — ${venue.city}` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choisis un lieu de la liste des casinos, ou laisse vide et précise un lieu libre ci-dessous.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <Trophy className="mr-2 text-blue-600" size={18} />
                      Série (optionnel)
                    </label>
                    {seriesError && (
                      <p className="text-red-500 text-sm mb-2">{seriesError}</p>
                    )}
                    <select
                      name="detailedInfo.seriesId"
                      value={formData.detailedInfo.seriesId}
                      onChange={handleChange}
                      disabled={seriesLoading}
                      className={`w-full px-4 py-3 border ${errors.seriesId ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">
                        {seriesLoading ? 'Chargement des séries...' : 'Tournoi indépendant (pas de série)'}
                      </option>
                      {series.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                      <option value={NEW_SERIES_VALUE}>+ Créer une nouvelle série...</option>
                    </select>
                    {formData.detailedInfo.seriesId === NEW_SERIES_VALUE && (
                      <input
                        type="text"
                        value={newSeriesName}
                        onChange={(e) => setNewSeriesName(e.target.value)}
                        placeholder="Ex: PokerStars Open Main Event"
                        className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    {errors.seriesId && <p className="text-red-500 text-sm mt-1">{errors.seriesId}</p>}
                    {!!formData.detailedInfo.seriesId && (
                      <div className="mt-3">
                        <label className="block text-xs text-gray-500 mb-1">
                          Nombre total d'entrées de la série (optionnel)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={seriesTotalEntries}
                          onChange={(e) => setSeriesTotalEntries(e.target.value)}
                          placeholder="Ex: 450"
                          className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Nombre total de joueurs ayant pris part à l'ensemble de la série (tous days
                          confondus), généralement connu une fois la série terminée. Partagé par tous
                          les days de cette série, pas propre à ce tournoi.
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Pour une structure multi-jours (ex: Day 1A, Day 1B, Final Day), rattache ce tournoi
                      à une série avec son propre nom (ex: "PokerStars Open Main Event") : les
                      inscriptions correspondantes seront regroupées dans "Mes participations" au lieu
                      d'apparaître comme des tournois sans lien entre eux.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="mr-2 text-blue-600" size={18} />
                      Lieu (texte libre, si non listé ci-dessus)
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.location"
                      value={formData.detailedInfo.location}
                      onChange={handleChange}
                      placeholder="Casino Barrière Paris"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <Clock className="mr-2 text-blue-600" size={18} />
                      Durée
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.duration"
                      value={formData.detailedInfo.duration}
                      onChange={handleChange}
                      placeholder="2 jours (niveaux de 40 min)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <Users className="mr-2 text-blue-600" size={18} />
                      Organisateur
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.organizer"
                      value={formData.detailedInfo.organizer}
                      onChange={handleChange}
                      placeholder="Winamax & Texapoker"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      <DollarSign className="mr-2 text-green-600" size={18} />
                      Buy-in
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      name="detailedInfo.buyInAmount"
                      value={formData.detailedInfo.buyInAmount}
                      onChange={handleChange}
                      placeholder="Ex: 500 (en €)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.detailedInfo.buyIn && formData.detailedInfo.buyInAmount === '' && (
                      <p className="text-xs text-amber-700 mt-1">
                        Ancien buy-in en texte libre : « {formData.detailedInfo.buyIn} ». Saisis le montant
                        en euros pour que « Buy-in payé » soit calculé automatiquement dans « Mes participations ».
                      </p>
                    )}
                    <label className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        name="detailedInfo.hasOwnBuyIn"
                        checked={formData.detailedInfo.hasOwnBuyIn}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Ce day a son propre buy-in/inscription
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Décoche pour un day comme un Final Day auquel on accède sans nouvelle inscription
                      (qualifié depuis un day précédent) : les champs "Mes entrées"/"Buy-in payé" et le
                      bouton "Enregistrer" seront alors masqués pour ce day dans "Mes participations".
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      Prize Pool
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.prizePool"
                      value={formData.detailedInfo.prizePool}
                      onChange={handleChange}
                      placeholder="100 000€ garantis"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      Starting Stack
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.startingStack"
                      value={formData.detailedInfo.startingStack}
                      onChange={handleChange}
                      placeholder="30 000 jetons"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                      Structure des Blinds
                    </label>
                    <input
                      type="text"
                      name="detailedInfo.blindStructure"
                      value={formData.detailedInfo.blindStructure}
                      onChange={handleChange}
                      placeholder="100/200 - 40 min"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Programme / Agenda */}
              <div className="border-b pb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-1">Programme / Agenda</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Si ce tournoi fait partie d'une série de plusieurs jours, le programme affiché aux joueurs
                  est celui de la série (jours et horaires). Ce champ ne sert alors qu'aux tournois indépendants.
                </p>
                {formData.detailedInfo.agenda.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange('agenda', index, e.target.value)}
                      placeholder="Day 1A - Vendredi 15h00"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.detailedInfo.agenda.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('agenda', index)}
                        className="bg-red-600 text-white px-4 rounded-lg hover:bg-red-700 transition"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('agenda')}
                  className="text-blue-600 text-sm font-semibold hover:underline"
                >
                  + Ajouter une ligne au programme
                </button>
              </div>

              {/* Notes */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="mr-2 text-blue-600" size={18} />
                  Notes importantes
                </label>
                <textarea
                  name="detailedInfo.notes"
                  value={formData.detailedInfo.notes}
                  onChange={handleChange}
                  placeholder="Informations complémentaires, conditions particulières..."
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments`)}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={isSaving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center disabled:opacity-50"
                >
                  <Save className="mr-2 flex-shrink-0" size={20} />
                  {isSaving ? 'Enregistrement...' : isEditMode ? (
                    <>
                      <span className="sm:hidden">Enregistrer</span>
                      <span className="hidden sm:inline">Enregistrer les modifications</span>
                    </>
                  ) : 'Créer le tournoi'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEditTournamentPage;
